import React, { useState } from 'react';
import { Input, Select } from '@/components/common/FormField';
import { 
  PartnerQueryParams,
  PartnerType,
  PARTNER_TYPE_OPTIONS,
  PARTNER_STATUS_OPTIONS,
} from '@/app/types/partners';

import './PartnerFilters.scss';

interface PartnerFiltersProps {
  filters: PartnerQueryParams;
  onFiltersChange: (filters: PartnerQueryParams) => void;
  onSearch: () => void;
  onClear: () => void;
}

export const PartnerFilters: React.FC<PartnerFiltersProps> = ({
  filters,
  onFiltersChange,
  onSearch,
  onClear,
}) => {
  const [isCollapsed, setIsCollapsed] = useState(true);

  const handleChange = <K extends keyof PartnerQueryParams>(
    key: K,
    value: PartnerQueryParams[K]
  ) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const handleStatusChange = (value: string) => {
    if (value === '') {
      handleChange('is_active', undefined);
    } else {
      handleChange('is_active', value === 'true');
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      onSearch();
    }
  };

  const hasActiveFilters = Boolean(
    filters.partner_type ||
    filters.is_active !== undefined ||
    filters.search
  );

  // Convert is_active boolean to string for select
  const statusValue = filters.is_active === undefined 
    ? '' 
    : filters.is_active ? 'true' : 'false';

  return (
    <div className="partner-filters" onKeyPress={handleKeyPress}>
      {/* Mobile Toggle Header */}
      <button 
        className="filters-toggle"
        onClick={() => setIsCollapsed(!isCollapsed)}
        aria-expanded={!isCollapsed}
      >
        <span className="toggle-text">
          🔍 Filters
          {hasActiveFilters && <span className="active-indicator">●</span>}
        </span>
        <span className={`toggle-chevron ${isCollapsed ? '' : 'expanded'}`}>▼</span>
      </button>

      {/* Filter Content - Collapsible on Mobile */}
      <div className={`filters-content ${isCollapsed ? 'collapsed' : ''}`}>
        <div className="filters-row">
          <Input
            label="Search"
            value={filters.search || ''}
            onChange={(val) => handleChange('search', val || undefined)}
            placeholder="Search by name, DBA, or ID"
          />

          <Select
            label="Partner Type"
            value={filters.partner_type || ''}
            onChange={(val) => handleChange('partner_type', (val as PartnerType) || undefined)}
            options={PARTNER_TYPE_OPTIONS.map(opt => ({
              value: opt.value,
              label: opt.label,
            }))}
          />

          <Select
            label="Status"
            value={statusValue}
            onChange={handleStatusChange}
            options={PARTNER_STATUS_OPTIONS.map(opt => ({
              value: opt.value,
              label: opt.label,
            }))}
          />

          <div className="filter-actions">
            <button className="btn-primary" onClick={onSearch}>
              Search
            </button>
            <button className="btn-secondary" onClick={onClear}>
              Clear
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerFilters;

